/**
 * Voice capture — the audio→Transcribe poll loop, extracted as a pure,
 * injectable function so it can be unit-tested without React, real timers, or
 * the network (mock `ingest` + inject `sleep`).
 *
 * Flow (backend = ingestion-agent `handleVoice`, verified 2026-06-18):
 *   ingest({audioBase64, format})         → 202 { jobName, status:'IN_PROGRESS' }
 *   ingest({transcriptionJobName})        → { status:'IN_PROGRESS' }
 *                                         | { status:'COMPLETED', transcript, expense }
 *                                         | { status:'FAILED' }
 * The 202 + poll deliberately dodges the 29s API-Gateway cap (each call is fast).
 * The parsed expense is NOT persisted by the backend — the caller pre-fills the
 * manual form and the user submits it (the one path that writes to /expenses).
 */

export interface VoiceIngestResponse {
  jobName?: string;
  status?: string;
  transcript?: string;
  expense?: Record<string, unknown>;
  message?: string;
}

export type VoiceIngestFn = (data: {
  audioS3Key?: string;
  audioBase64?: string;
  transcript?: string;
  format?: string;
  transcriptionJobName?: string;
}) => Promise<unknown>;

export interface VoiceCaptureResult {
  success: boolean;
  expense?: Record<string, unknown>;
  transcript?: string;
  error?: string;
}

export interface TranscribeOpts {
  intervalMs?: number; // poll interval, default 3000
  maxPolls?: number; // default 20 (~60s)
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function errMsg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Start a transcription job (from an S3 key — the WAF-safe path — or a small
 * base64 clip) and poll until the transcript + parsed expense come back.
 * Returns a flat result; the caller decides how to surface it.
 */
export async function transcribeVoiceAudio(
  ingest: VoiceIngestFn,
  startPayload: { audioS3Key?: string; audioBase64?: string; format?: string },
  opts: TranscribeOpts = {},
): Promise<VoiceCaptureResult> {
  const intervalMs = opts.intervalMs ?? 3000;
  const maxPolls = opts.maxPolls ?? 20;
  const sleep = opts.sleep ?? defaultSleep;

  if (!startPayload.audioS3Key && !startPayload.audioBase64) {
    return { success: false, error: 'No audio was recorded. Please try again.' };
  }

  let start: VoiceIngestResponse;
  try {
    start = (await ingest(startPayload)) as VoiceIngestResponse;
  } catch (err) {
    return { success: false, error: errMsg(err, 'Could not start transcription. Please type it instead.') };
  }

  // Defensive: a synchronous expense (shouldn't happen for audio, but tolerate it).
  if (start?.expense) {
    return { success: true, expense: start.expense, transcript: start.transcript };
  }

  const jobName = start?.jobName;
  if (!jobName) {
    return { success: false, error: 'Transcription did not start. Please type it instead.' };
  }

  for (let i = 0; i < maxPolls; i++) {
    await sleep(intervalMs);
    let poll: VoiceIngestResponse;
    try {
      poll = (await ingest({ transcriptionJobName: jobName })) as VoiceIngestResponse;
    } catch (err) {
      return { success: false, error: errMsg(err, 'Could not check the transcription. Please type it instead.') };
    }
    if (poll?.status === 'COMPLETED' && poll.expense) {
      return { success: true, expense: poll.expense, transcript: poll.transcript };
    }
    if (poll?.status === 'FAILED') {
      return { success: false, error: 'Could not understand the audio. Please type it instead.' };
    }
  }

  return { success: false, error: 'Transcription is taking too long. Please type it instead.' };
}

/**
 * Map a free-form (Claude-parsed) expense category to one of the Capture
 * manual-form `<select>` option labels, so a voice/typed result can pre-fill
 * the category. Returns '' (no match) → the user picks it on review.
 *
 * The returned strings MUST stay in lockstep with the <option> values in
 * src/pages/Capture.tsx (pinned by src/test/voice-capture.test.ts).
 */
export function mapVoiceCategoryToOption(category: string): string {
  const c = String(category || '').toLowerCase();
  if (!c) return '';
  const has = (...needles: string[]) => needles.some((n) => c.includes(n));

  if (has('malpractice')) return 'Malpractice Insurance';
  if (has('cme', 'conference', 'seminar', 'continuing education', 'course', 'training')) return 'CME Conference';
  if (has('license', 'dea', 'board cert', 'credential')) return 'Medical License';
  if (has('lodg', 'hotel', 'motel', 'airbnb', 'inn', 'resort')) return 'Travel Lodging';
  if (has('meal', 'lunch', 'dinner', 'breakfast', 'food', 'restaurant', 'cafe', 'coffee', 'catering')) return 'Business Meals (Travel)';
  if (has('transport', 'uber', 'lyft', 'taxi', 'cab', 'flight', 'airline', 'rental car', 'mileage', 'gas', 'fuel', 'parking', 'toll', 'train')) return 'Travel Transportation';
  if (has('software', 'saas', 'subscription', 'technology', 'computer', 'laptop', 'app', 'cloud')) return 'Technology / Software';
  if (has('dues', 'membership', 'society', 'association')) return 'Professional Dues';
  if (has('home office')) return 'Home Office';
  if (has('office', 'supply', 'supplies', 'stationery', 'printer')) return 'Office Supplies';
  return '';
}
